/**
 * Healix Custom Build — Shop by Color (mega menu + mobile drawer)
 *
 * Performance strategy:
 * 1. On first visit, fetches all products from configured collections,
 *    calls the swatch API, processes into Core/Limited groups, then
 *    caches both the processed data AND the rendered HTML in sessionStorage.
 * 2. On every subsequent page load within the session, renders instantly
 *    from cached HTML — zero network requests, zero delay.
 * 3. A background refresh runs silently after render to pick up any
 *    new colors added since the session started.
 */
(function () {
  if (window.__healixMegaMenuColorsInit) return;
  window.__healixMegaMenuColorsInit = true;
  var CACHE_KEY = 'healix_sbc_data';
  var CACHE_HTML_MEGA = 'healix_sbc_mega';
  var CACHE_HTML_DRAWER = 'healix_sbc_drawer';
  var CACHE_TTL = 30 * 60 * 1000; // 30 minutes

  var COLOR_MAP = {
    codeblack: 'black',
    clinicblue: 'clinical-blue',
    olivedrip: 'olive-drip',
    carboncalm: 'carbon-calm',
    vitalsnavy: 'navy',
    nightgrape: 'night-grape',
    kindmauve: 'kind-mauve',
    coreblue: 'royal-blue',
    rapidred: 'rapid-red-women',
    midnightsurge: 'midnight-surge-women',
    tealtonic: 'teal-tonic',
    goldenhour: 'golden-hour',
    perihustle: 'peri-hustle',
    lavenderstat: 'lavendar-stat',
    surgicalteal: 'surgical-teal',
    plumpulse: 'plum-pulse',
    pecandrift: 'pecan-drift',
    pulsepink: 'pulse-pink',
    shiftsapphire: 'shift-sapphire-women'
  };

  function esc(s) {
    var d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  }

  function parseCollections(attr) {
    return (attr || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean);
  }

  function readCache() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var cached = JSON.parse(raw);
      if (Date.now() - cached.ts > CACHE_TTL) return null;
      return cached.data;
    } catch (e) { return null; }
  }

  function writeCache(data) {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: data }));
    } catch (e) {}
  }

  function readHTMLCache(key) {
    try {
      var raw = sessionStorage.getItem(key);
      if (!raw) return null;
      var cached = JSON.parse(raw);
      if (Date.now() - cached.ts > CACHE_TTL) return null;
      return cached.html;
    } catch (e) { return null; }
  }

  function writeHTMLCache(key, html) {
    try {
      sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), html: html }));
    } catch (e) {}
  }

  function prettyName(s) {
    return String(s || 'Color').replace(/_/g, ' ').trim() || 'Color';
  }

  // Build Core/Limited colour groups straight from the palette — ONE cached
  // request via the shared library (GET /v1/colors). No per-collection product
  // fetches and no all-handles siblings batch (that was the heavy, high-
  // concurrency path). Only colours present in COLOR_MAP are shown (each links
  // to its colour collection); edition (core/limited) comes from the palette.
  function fetchFromNetwork() {
    return window.HealixColors.colors().then(function (list) {
      var coreByColor = {}, leByColor = {};
      (list || []).forEach(function (c) {
        var slug = String(c.slug || '').trim().toLowerCase();
        var mapped = COLOR_MAP[slug];
        if (!mapped || coreByColor[slug] || leByColor[slug]) return;
        var item = {
          collectionHandle: mapped,
          colorName: prettyName(c.name || c.slug),
          colorCode: String(c.hex || '').trim() || '#cccccc',
          isLimited: c.edition === 'limited'
        };
        if (item.isLimited) leByColor[slug] = item; else coreByColor[slug] = item;
      });
      var core = Object.keys(coreByColor).map(function (k) { return coreByColor[k]; });
      var limited = Object.keys(leByColor).map(function (k) { return leByColor[k]; });
      var data = { core: core, limited: limited };
      writeCache(data);
      return data;
    }).catch(function () { return { core: [], limited: [] }; });
  }

  function renderColorGrid(items, showNames, prefix) {
    var html = '';
    items.forEach(function (item) {
      html += '<a href="/collections/' + esc(item.collectionHandle) + '" class="' + prefix + '__color-item" title="' + esc(item.colorName) + '">';
      html += '<span class="' + prefix + '__color-dot" style="--dot-color: ' + esc(item.colorCode) + ';"></span>';
      if (showNames) {
        html += '<span class="' + prefix + '__color-name">' + esc(item.colorName) + '</span>';
      }
      html += '</a>';
    });
    return html;
  }

  function renderGroupedHTML(core, limited, showNames, prefix) {
    var html = '';
    var hasCore = core.length > 0;
    var hasLimited = limited.length > 0;

    if (hasCore) {
      html += '<div class="' + prefix + '__color-group">';
      if (hasLimited) {
        html += '<div class="' + prefix + '__color-group-header">';
        html += '<span class="' + prefix + '__color-badge ' + prefix + '__color-badge--core">Core</span>';
        html += '<span class="' + prefix + '__color-group-subtitle">Always available</span>';
        html += '</div>';
      }
      html += '<div class="' + prefix + '__color-grid">';
      html += renderColorGrid(core, showNames, prefix);
      html += '</div></div>';
    }

    if (hasLimited) {
      html += '<div class="' + prefix + '__color-group">';
      if (hasCore) {
        html += '<div class="' + prefix + '__color-group-header">';
        html += '<span class="' + prefix + '__color-badge ' + prefix + '__color-badge--limited">Limited</span>';
        html += '<span class="' + prefix + '__color-group-subtitle">Seasonal drops</span>';
        html += '</div>';
      }
      html += '<div class="' + prefix + '__color-grid">';
      html += renderColorGrid(limited, showNames, prefix);
      html += '</div></div>';
    }

    if (!hasCore && !hasLimited) {
      html = '<div class="' + prefix + '__color-loading">No colors available</div>';
    }

    return html;
  }

  function init() {
    var megaPanel = document.querySelector('[data-mega-color-panel]');
    var drawerSection = document.querySelector('[data-drawer-color-section]');
    if (!megaPanel && !drawerSection) return;

    var megaMount = megaPanel ? megaPanel.querySelector('[data-mega-color-mount]') : null;
    var drawerMount = drawerSection ? drawerSection.querySelector('[data-drawer-color-mount]') : null;
    var showNames = megaPanel ? megaPanel.getAttribute('data-show-names') === 'true' : true;

    // Collect all collection handles from both sources (deduped)
    var collectionsSet = {};
    var collections = [];
    [megaPanel, drawerSection].forEach(function (el) {
      if (!el) return;
      parseCollections(el.getAttribute('data-collections')).forEach(function (c) {
        if (!collectionsSet[c]) { collectionsSet[c] = true; collections.push(c); }
      });
    });

    // Try instant render from cached HTML
    var megaCacheHit = false;
    var drawerCacheHit = false;
    if (megaMount) {
      var cachedMega = readHTMLCache(CACHE_HTML_MEGA);
      if (cachedMega) { megaMount.innerHTML = cachedMega; megaCacheHit = true; }
    }
    if (drawerMount) {
      var cachedDrawer = readHTMLCache(CACHE_HTML_DRAWER);
      if (cachedDrawer) { drawerMount.innerHTML = cachedDrawer; drawerCacheHit = true; }
    }

    // If both hit cache, do a silent background refresh
    if (megaCacheHit && drawerCacheHit) {
      setTimeout(function () {
        fetchFromNetwork().then(function (groups) {
          if (megaMount) {
            var html = renderGroupedHTML(groups.core, groups.limited, showNames, 'mega-menu');
            megaMount.innerHTML = html;
            writeHTMLCache(CACHE_HTML_MEGA, html);
          }
          if (drawerMount) {
            var dhtml = renderGroupedHTML(groups.core, groups.limited, true, 'drawer');
            drawerMount.innerHTML = dhtml;
            writeHTMLCache(CACHE_HTML_DRAWER, dhtml);
          }
        });
      }, 5000);
      return;
    }

    // Try rendering from cached data (no network, but need to build HTML)
    var cachedData = readCache();
    if (cachedData && (cachedData.core.length || cachedData.limited.length)) {
      if (megaMount) {
        var mhtml = renderGroupedHTML(cachedData.core, cachedData.limited, showNames, 'mega-menu');
        megaMount.innerHTML = mhtml;
        writeHTMLCache(CACHE_HTML_MEGA, mhtml);
      }
      if (drawerMount) {
        var dhtml = renderGroupedHTML(cachedData.core, cachedData.limited, true, 'drawer');
        drawerMount.innerHTML = dhtml;
        writeHTMLCache(CACHE_HTML_DRAWER, dhtml);
      }
      return;
    }

    // First visit: fetch from network
    fetchFromNetwork().then(function (groups) {
      if (megaMount) {
        var mhtml = renderGroupedHTML(groups.core, groups.limited, showNames, 'mega-menu');
        megaMount.innerHTML = mhtml;
        writeHTMLCache(CACHE_HTML_MEGA, mhtml);
      }
      if (drawerMount) {
        var dhtml = renderGroupedHTML(groups.core, groups.limited, true, 'drawer');
        drawerMount.innerHTML = dhtml;
        writeHTMLCache(CACHE_HTML_DRAWER, dhtml);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
